import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Repeat } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function RepetitionPower() {
  const navigate = useNavigate();

  return (
      <ProtectedLayout>  
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Repeat className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Why Repetition is the Real Superpower</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="bg-blue-50 p-6 rounded-lg mb-8">
              <h2 className="text-2xl font-semibold text-[#004060] mt-0 mb-4">Inside the JC Growth System</h2>
              <p className="text-gray-700 mb-0">
                Most people are looking for the big answer.<br />
                The perfect strategy.<br />
                The game-changing insight.<br />
                The secret move that will change everything overnight.
              </p>
            </div>

            <p className="text-gray-600 mb-8">
              But the truth — the thing no one tells you — is that the people who succeed in life, business, health, relationships... they're not chasing big answers.
            </p>

            <p className="text-gray-600 mb-8">
              They're practicing small ones.
            </p>

            <p className="text-gray-600 mb-8">
              Over and over again.
            </p>

            <div className="bg-purple-50 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-semibold text-[#004060] mt-0 mb-4">Mastery is Repetition with Meaning</h3>
              <p className="text-gray-700 mb-4">
                Anders Ericsson, who spent decades studying world-class performers, found that expertise doesn't come from talent. It comes from deliberate practice — doing small things, on purpose, with feedback, over time.
              </p>
              <p className="text-gray-700 mb-0 italic">
                "Consistently and purposefully practicing skills — even small ones — creates the conditions for mastery."
              </p>
            </div>

            <p className="text-gray-600 mb-4">
              Angela Duckworth's research on grit backs this up. Grit isn't just about working hard — it's about working on the right things long enough to get really good at them.
            </p>

            <p className="text-gray-600 mb-8 italic">
              "Grit grows when you show up for what matters — especially when it's hard or boring or not producing quick wins."
            </p>

            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-semibold text-[#004060] mt-0 mb-4">This is why the JC Growth System matters</h3>
              <p className="text-gray-700 mb-4">
                We built this system on the truth that growth comes from who you're becoming — not just what you're achieving.
              </p>
              <ul className="list-none p-0 space-y-2 text-gray-700">
                <li>Every time you celebrate a win — you train your brain to see progress.</li>
                <li>Every time you clarify your goals — you remind yourself of what matters.</li>
                <li>Every time you reflect on what's working — you sharpen your awareness.</li>
                <li>Every time you take action — you build courage, connection, and curiosity into your identity.</li>
              </ul>
            </div>

            <p className="text-gray-600 mb-2">
              Those actions are small.
            </p>
            <p className="text-gray-600 mb-2">
              Sometimes boring.
            </p>
            <p className="text-gray-600 mb-8">
              Often unsexy.
            </p>

            <p className="text-gray-600 mb-8">
              But that's exactly why they work.
            </p>

            <div className="bg-orange-50 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-semibold text-[#004060] mt-0 mb-4">Repetition is Trust in Action</h3>
              <div className="space-y-4 text-gray-700">
                <p className="italic">
                  Repetition says:<br />
                  "I trust that I'm growing, even when I can't see it yet."
                </p>
                <p className="italic">
                  Repetition says:<br />
                  "I'm willing to be bad before I'm good, and good before I'm great."
                </p>
                <p className="italic">
                  Repetition says:<br />
                  "My future is built one action at a time."
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-2">
              This system isn't about doing more.
            </p>
            <p className="text-gray-600 mb-8">
              It's about doing what matters — long enough for it to change you.
            </p>

            <div className="bg-gradient-to-r from-[#004060] to-[#236caf] text-white p-8 rounded-lg">
              <p className="text-xl font-medium mb-2">
                That's what creates confidence.
              </p>
              <p className="text-xl font-medium mb-2">
                That's what builds momentum.
              </p>
              <p className="text-xl font-medium mb-0">
                That's what forges mastery.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ProtectedLayout>
        );
}